import React, { useEffect, useState, useMemo, FC } from 'react'
import { makeStyles } from 'tss-react/mui'
import { Box, Typography } from '@mui/material'
import { omit } from 'lodash'
import { TFunction } from 'i18next'

import { Dialog } from '@e3dc-react/shell/Elements/Dialog'
import { DialogActions } from '@e3dc-react/shell/Elements/Dialog/Dialog'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { UserData } from 'src/State/User/UserModel'
import { useFetchUserData } from 'src/Hooks/useFetchUserData'
import { getFullName } from 'src/Libs/utils/string-functions'
import { useUserRoles } from 'src/Hooks/user'
import { ChangeReason, useChangeReasonFormInitializerZod, ChangeReasonForm } from 'src/Libs/form'
import { FormProvider, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { TextField } from 'src/Libs/inputs/react-hook-form'

const DEFAULT_REASON = 'Initialer Manager'

const useStyles = makeStyles()(theme => ({
  description: {
    marginTop: theme.spacing(4),
  },
  input: {
    marginBlock: theme.spacing(4),
  },
}))

export interface AssignManagerDialogProps {
  place?: number
  onClose?: () => void
  onConfirm: (reason: ChangeReason, removeOldManager: boolean, refreshTable?: () => void) => void
  companyName: string
  managerId?: number
  selectedUser: string
}

const getButtonLabel = (
  t: TFunction,
  managerData: UserData | undefined,
  isServiceUser: boolean,
  hasManager: boolean,
  selectedUser: string
): string => {
  if (isServiceUser) {
    /** In case of new company without manager. There are no manager to make installer / partner, so we show just OK button. */
    if (!hasManager) {
      return t('common.ok')
    }

    /** Assign manager permission as his user level (INSTALLER) */
    if (managerData?.level === 'INSTALLER' || managerData?.ipin) {
      return t('administration.companies.assignManager.make-installer')
    }

    /** Assign manager permission as his user level (PARTNER) */
    if (managerData?.level === 'PARTNER' || managerData?.ipin === null) {
      return t('administration.companies.assignManager.make-partner')
    }

    return t('common.ok')
  } else {
    return t('administration.companies.assignManager.confirm', { userName: selectedUser })
  }
}

/**
 * Dialog in which the service user can decide if the old manager should be removed from the company, made an installer or made a partner.
 * Can be accessed from service user options within the company permission page, via a three-dot menu
 * It presents a confirmation dialog with a mandatory "Reason" text area for users to justify the assign manager action.
 */
const AssignManagerDialog: FC<AssignManagerDialogProps> = ({ place, onClose, onConfirm, companyName, managerId, selectedUser }) => {
  const { t } = useCustomTranslation()
  const { classes } = useStyles()
  const { isServiceUser } = useUserRoles()
  const { fetchUser } = useFetchUserData()
  const [managerData, setManagerData] = useState<UserData>()
  const [hasManager, setHasManager] = useState(false)
  const { initialValues, validationSchema } = useChangeReasonFormInitializerZod(undefined, hasManager ? '' : DEFAULT_REASON)

  const methods = useForm<ChangeReasonForm<ChangeReason>>({
    defaultValues: initialValues,
    mode: 'onChange',
    resolver: zodResolver(validationSchema),
  })

  const onSave =
    (removeOldManager: boolean) =>
    (values: ChangeReasonForm<ChangeReason>): void => {
      onConfirm({ reason: values.reason }, removeOldManager)
      onClose?.()
    }

  useEffect(() => {
    methods.reset(initialValues)
  }, [methods, hasManager]) //eslint-disable-line react-hooks/exhaustive-deps

  const omittedActions: (keyof DialogActions)[] = !hasManager || !isServiceUser ? ['additional'] : []

  const description = useMemo(() => {
    return isServiceUser
      ? t('administration.companies.assignManager.service-description', {
          managerName: `${getFullName(managerData?.Vorname, managerData?.Name) ?? '-'}`,
          userLevel: t(`framework.userBadge.userlevel.${managerData?.level}`),
        })
      : t('administration.companies.assignManager.manager-description')
  }, [isServiceUser, managerData, t])

  const confirmationText = useMemo(() => {
    return isServiceUser
      ? t('administration.companies.assignManager.service-confirmation-text', { companyName: companyName })
      : t('administration.companies.assignManager.company-name-description', { companyName: companyName, userName: selectedUser })
  }, [companyName, isServiceUser, selectedUser, t])

  useEffect(() => {
    if (!managerId) return

    const getManagerData = async (managerId: number): Promise<void> => {
      try {
        const managerUserData = await fetchUser(managerId)
        setManagerData(managerUserData?.userData)
        setHasManager(true)
      } catch (error) {
        setHasManager(false)
        console.error('Error fetching manager data:', error)
      }
    }

    getManagerData(managerId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [managerId])

  return (
    <FormProvider {...methods}>
      <Dialog
        place={place}
        title={t('administration.companies.assignManager.title')}
        maxWidth={'sm'}
        onClose={() => {
          onClose?.()
        }}
        actions={omit(
          {
            secondary: {
              name: t('common.cancel'),
              onClick: () => {
                onClose?.()
              },
            },
            primary: {
              name: getButtonLabel(t, managerData, isServiceUser, hasManager, selectedUser),
              disabled: !methods.formState.isValid || (hasManager && !methods.formState.isDirty),
              onClick: () => {
                methods.handleSubmit(onSave(false))()
              },
            },
            additional: {
              name: t('administration.companies.assignManager.remove-user'),
              disabled: !methods.formState.isValid || !methods.formState.isDirty,
              onClick: () => {
                methods.handleSubmit(onSave(true))()
              },
            },
          },
          omittedActions
        )}
      >
        <Box className={classes.description}>
          <Typography>{confirmationText}</Typography>
        </Box>
        {hasManager && (
          <>
            <TextField className={classes.input} label={t('administration.companies.empl-history.reason')} name="reason" multiline fullWidth />
            <Typography>{description}</Typography>
          </>
        )}
      </Dialog>
    </FormProvider>
  )
}

export default AssignManagerDialog
